import React from 'react';
import { useTranslation } from 'react-i18next';
import { 
  ChartBarIcon,
  BanknotesIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  ReceiptRefundIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../../ui/LoadingSpinner';

const AccountingDashboard = ({ financialData, rawData, loading, setActiveTab }) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'SAR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const getMetricCard = (title, value, icon, color, trend = null) => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs font-medium text-gray-600 truncate">{title}</p>
          <p className="text-lg sm:text-xl font-bold text-gray-900 truncate">{value}</p>
          {trend && (
            <div className={`flex items-center text-xs ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
              {trend > 0 ? (
                <ArrowTrendingUpIcon className="w-3.5 h-3.5 mr-0.5" />
              ) : (
                <ArrowTrendingDownIcon className="w-3.5 h-3.5 mr-0.5" />
              )}
              {Math.abs(trend)}%
            </div>
          )}
        </div>
        <div className={`p-2 rounded-full flex-shrink-0 ${color}`}>
          {icon}
        </div>
      </div>
    </div>
  );

  const getQuickActionCard = (title, description, icon, onClick, color = 'bg-blue-50 text-blue-600') => (
    <div
      className={`${color} rounded-lg p-3 cursor-pointer hover:opacity-80 transition-opacity`}
      onClick={onClick}
    >
      <div className="flex items-center gap-2">
        <div className="flex-shrink-0">{icon}</div>
        <div className="min-w-0">
          <h3 className="text-sm font-medium truncate">{title}</h3>
          <p className="text-xs opacity-75 truncate">{description}</p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-3 sm:space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <h2 className="text-base sm:text-lg font-bold text-gray-900 truncate">{t('accounting.dashboard_title', 'Financial Dashboard')}</h2>
          <p className="text-xs text-gray-600 truncate">{t('accounting.dashboard_subtitle', 'Overview of your financial performance')}</p>
        </div>
        <div className="text-xs text-gray-500 flex-shrink-0 hidden sm:block">
          {t('dashboard.last_updated')}: {new Date().toLocaleTimeString()}
        </div>
      </div>

      {/* Key Financial Metrics */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-2">{t('dashboard.key_metrics')}</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
          {getMetricCard(
            t('accounting.revenue'),
            formatCurrency(financialData.revenue),
            <BanknotesIcon className="w-5 h-5 text-green-600" />,
            'bg-green-50',
            financialData.revenue > 0 ? 12 : null
          )}
          {getMetricCard(
            t('accounting.expenses', 'Expenses'),
            formatCurrency(financialData.expenses),
            <ArrowTrendingDownIcon className="w-5 h-5 text-red-600" />,
            'bg-red-50',
            financialData.expenses > 0 ? -8 : null
          )}
          {getMetricCard(
            t('accounting.profit', 'Profit'),
            formatCurrency(financialData.profit),
            <ChartBarIcon className="w-5 h-5 text-blue-600" />,
            'bg-blue-50',
            financialData.profit > 0 ? 15 : -5
          )}
          {getMetricCard(
            t('accounting.cash_flow', 'Cash Flow'),
            formatCurrency(financialData.cashFlow),
            <CurrencyDollarIcon className="w-5 h-5 text-purple-600" />,
            'bg-purple-50'
          )}
        </div>
      </div>

      {/* Financial Status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
        {/* Receivables & Payables */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-2">{t('accounting.receivables_payables', 'Receivables & Payables')}</h3>
          <div className="space-y-2">
            <div className="flex items-center justify-between p-2 bg-green-50 rounded-md">
              <div className="flex items-center gap-2">
                <CheckCircleIcon className="w-4 h-4 text-green-600" />
                <div>
                  <p className="font-medium text-green-900">{t('accounting.accounts_receivable', 'Accounts Receivable')}</p>
                  <p className="text-sm text-green-600">{formatCurrency(financialData.receivables)}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between p-2 bg-orange-50 rounded-md">
              <div className="flex items-center gap-2">
                <ExclamationTriangleIcon className="w-5 h-5 text-orange-600" />
                <div>
                  <p className="font-medium text-orange-900">{t('accounting.accounts_payable', 'Accounts Payable')}</p>
                  <p className="text-sm text-orange-600">{formatCurrency(financialData.payables)}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between p-2 bg-blue-50 rounded-md">
              <div className="flex items-center gap-2">
                <ClockIcon className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="font-medium text-blue-900">{t('accounting.outstanding_invoices', 'Outstanding Invoices')}</p>
                  <p className="text-sm text-blue-600">{formatCurrency(financialData.outstandingInvoices)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* VAT & Tax Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-2">{t('accounting.tax_information', 'Tax Information')}</h3>
          <div className="space-y-2">
            <div className="flex items-center justify-between p-2 bg-red-50 rounded-md">
              <div className="flex items-center gap-2">
                <ExclamationTriangleIcon className="w-5 h-5 text-red-600" />
                <div>
                  <p className="font-medium text-red-900">{t('accounting.vat_payable', 'VAT Payable')}</p>
                  <p className="text-sm text-red-600">{formatCurrency(financialData.vatPayable)}</p>
                </div>
              </div>
            </div>
            {financialData.cash && (
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <BanknotesIcon className="w-4 h-4 text-green-600" />
                  <div>
                    <p className="font-medium text-green-900">{t('accounting.cash_balance', 'Cash Balance')}</p>
                    <p className="text-sm text-green-600">{formatCurrency(financialData.cash)}</p>
                  </div>
                </div>
              </div>
            )}
            {financialData.grossMargin && (
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <ChartBarIcon className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="font-medium text-blue-900">{t('accounting.gross_margin', 'Gross Margin')}</p>
                    <p className="text-sm text-blue-600">{financialData.grossMargin.toFixed(1)}%</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-2">{t('accounting.quick_actions', 'Quick Actions')}</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
          {getQuickActionCard(
            t('accounting.create_invoice', 'Create Invoice'),
            t('accounting.create_invoice_desc', 'Generate a new invoice'),
            <DocumentTextIcon className="w-5 h-5" />,
            () => setActiveTab('invoices'),
            'bg-blue-50 text-blue-600'
          )}
          {getQuickActionCard(
            t('accounting.add_expense', 'Add Expense'),
            t('accounting.add_expense_desc', 'Record a new expense'),
            <ReceiptRefundIcon className="w-5 h-5" />,
            () => setActiveTab('expenses'),
            'bg-green-50 text-green-600'
          )}
          {getQuickActionCard(
            t('accounting.journal_entry', 'Journal Entry'),
            t('accounting.journal_entry_desc', 'Create a journal entry'),
            <CurrencyDollarIcon className="w-5 h-5" />,
            () => setActiveTab('journal'),
            'bg-purple-50 text-purple-600'
          )}
          {getQuickActionCard(
            t('accounting.view_reports', 'View Reports'),
            t('accounting.view_reports_desc', 'Access financial reports'),
            <ChartBarIcon className="w-5 h-5" />,
            () => setActiveTab('reports'),
            'bg-orange-50 text-orange-600'
          )}
        </div>
      </div>

      {/* Operational Insights */}
      {rawData && (rawData.shipments || rawData.costings || rawData.drivers) && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-2">{t('accounting.operational_insights', 'Operational Insights')}</h3>
          <div className="grid grid-cols-3 gap-2 sm:gap-3">
            {rawData.shipments && (
              <div className="text-center p-2 sm:p-3 bg-blue-50 rounded-md">
                <div className="text-lg sm:text-xl font-bold text-blue-600">{rawData.shipments.length}</div>
                <div className="text-xs text-blue-800">{t('clients.total_shipments')}</div>
              </div>
            )}
            {rawData.costings && (
              <div className="text-center p-2 sm:p-3 bg-green-50 rounded-md">
                <div className="text-lg sm:text-xl font-bold text-green-600">{rawData.costings.length}</div>
                <div className="text-xs text-green-800">{t('accounting.cost_records', 'Cost Records')}</div>
              </div>
            )}
            {rawData.drivers && (
              <div className="text-center p-2 sm:p-3 bg-purple-50 rounded-md">
                <div className="text-lg sm:text-xl font-bold text-purple-600">{rawData.drivers.length}</div>
                <div className="text-xs text-purple-800">{t('drivers.active_drivers')}</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountingDashboard;
